import { generateEmbedUrl, EmbedConfig } from '../../utils/urlGenerator';
import { useRef, useEffect, useState } from 'react';
import { EMBED_BASE_URL } from '../../../../config';

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * Config Preview Component
 *
 * Renders a live iframe preview of the configurator with the current configuration.
 * Updates in real-time as the user changes settings in the dashboard.
 */

interface ConfigPreviewProps {
  config: EmbedConfig;
  configuringProductTag?: string;
}

export default function ConfigPreview({
  config,
  configuringProductTag
}: ConfigPreviewProps) {
  // Inject the dashboard-only preview hint so the iframe loads on the product
  // the partner is currently configuring, not necessarily the default product.
  const previewConfig: EmbedConfig = configuringProductTag
    ? { ...config, previewProductTag: configuringProductTag }
    : config;
  const embedUrl = generateEmbedUrl(EMBED_BASE_URL, previewConfig);
  // Debounce the key so the iframe only remounts 500ms after changes stop.
  // This prevents constant reloads while dragging color pickers, while still
  // ensuring the preview always settles to the current URL.
  const iframeKey = useDebounce(embedUrl, 500);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Get configured dimensions (fallback to defaults if not set)
  const configuredWidth = parseInt(config.width || '1200');
  const configuredHeight = parseInt(config.height || '800');

  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;

      // Calculate scale based on width only
      const scaleX = containerWidth / configuredWidth;
      const scaleY = containerHeight / configuredHeight;

      // Never scale up, only down
      const newScale = Math.min(scaleX, scaleY, 1);
      setScale(newScale);
    };

    updateScale();

    const resizeObserver = new ResizeObserver(updateScale);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [configuredWidth, configuredHeight]);

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full items-center justify-center"
      style={{ marginTop: '-4px' }}
    >
      <div
        style={{
          width: `${configuredWidth}px`,
          height: '100%',
          maxHeight: `${containerRef?.current?.clientHeight}px`,
          transform: `scale(${scale})`,
          transformOrigin: 'center top'
        }}
      >
        <iframe
          key={iframeKey}
          src={embedUrl}
          width={configuredWidth}
          height={configuredHeight}
          className="rounded-lg"
          title="Configurator Preview"
          sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation allow-top-navigation-by-user-activation"
          allow="clipboard-write"
          style={{ border: 0 }}
        />
      </div>
    </div>
  );
}
